import React, { useEffect } from 'react';
import { DEFAULT_ACCOUNT_NAMES_BY_POOL_ID } from 'src/constants/constants';
import { MAIN_POOL_ID } from '@drift/common';
import { isAmplifyAccountName } from 'src/utils/amplify';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import useTickedAllSubAccounts from 'src/hooks/useTickedAllSubAccounts';
import { CollateralSourceSelector } from 'src/components/Inputs/CollateralSourceSelector';
import InlineAlert from 'src/components/primitives/InlineAlert/InlineAlert';
import { AccountData } from 'src/stores/useDriftAccountsStore';

interface SubAccountSelectorProps {
	selectedTargetAccountKey: string;
	selectedPoolId: number;
	handleTargetAccountChange: (newAccountKey: string) => void;
	mode: 'deposit' | 'withdraw';
	newSubaccount?:
		| {
				isNewAccount: false;
		  }
		| {
				isNewAccount: true;
				defaultName: string;
		  };
	includeDelegates?: boolean;
	accountLabel?: string;
	/** Optional filter for which accounts to show */
	filterAccount?: (account: AccountData) => boolean;
}

const SubAccountSelector = ({
	selectedTargetAccountKey,
	selectedPoolId,
	handleTargetAccountChange,
	mode,
	newSubaccount,
	includeDelegates = false,
	accountLabel,
	filterAccount,
}: SubAccountSelectorProps) => {
	const allSubaccounts = useTickedAllSubAccounts();
	const availableSubaccounts = filterAccount
		? allSubaccounts.filter(filterAccount)
		: allSubaccounts;

	// When filtered, reset selection if current account is no longer in the list
	useEffect(() => {
		if (
			!filterAccount ||
			availableSubaccounts.length === 0 ||
			availableSubaccounts.some((a) => a.userKey === selectedTargetAccountKey)
		)
			return;
		handleTargetAccountChange(availableSubaccounts[0].userKey);
	}, [
		filterAccount,
		availableSubaccounts,
		selectedTargetAccountKey,
		handleTargetAccountChange,
	]);

	const currentUserName = useDriftAccountStore(
		(s) => s.accounts[selectedTargetAccountKey ?? '']?.name ?? ''
	);

	const isDepositingToIsolatedPool = selectedPoolId !== MAIN_POOL_ID;
	const isAmplifyAccount = isAmplifyAccountName(currentUserName);

	const displayNewSubaccountName =
		mode === 'deposit' &&
		newSubaccount?.isNewAccount &&
		availableSubaccounts.length > 0;

	const displaySubaccountSelector =
		mode === 'deposit'
			? !newSubaccount?.isNewAccount && availableSubaccounts.length > 1
			: availableSubaccounts.length > 1;

	const labelText =
		accountLabel ?? (mode === 'deposit' ? 'Deposit to' : 'Withdraw from');

	return (
		<div className="flex flex-col gap-1">
			{displayNewSubaccountName && newSubaccount?.isNewAccount && (
				<div className="rounded-sm border border-input-border bg-input-bg p-md">
					<span className="typo-b3 text-text-default">
						{newSubaccount.defaultName}
					</span>
				</div>
			)}

			{displaySubaccountSelector && (
				<CollateralSourceSelector
					label={mode === 'withdraw' ? labelText : undefined}
					onChange={(newAccountKey) => {
						handleTargetAccountChange(newAccountKey);
					}}
					includeDelegates={includeDelegates}
					options={availableSubaccounts}
					initialSelection={selectedTargetAccountKey}
				/>
			)}

			{!displaySubaccountSelector && !displayNewSubaccountName && (
				<div className="rounded-sm border border-input-border bg-input-bg p-md">
					<span className="typo-b3 text-text-default">
						{isAmplifyAccount
							? `Amplify ${currentUserName.replace('Amplify ', '')}`
							: currentUserName ||
								DEFAULT_ACCOUNT_NAMES_BY_POOL_ID[selectedPoolId]}
					</span>
				</div>
			)}

			{mode === 'deposit' && isDepositingToIsolatedPool && (
				<InlineAlert
					variant="warning"
					title="You are depositing to an Isolated Pool. Deposits can only be used as collateral for borrowing within this pool."
					className="mt-2xs"
				/>
			)}
		</div>
	);
};

export default SubAccountSelector;
